import { Component, inject, OnInit, signal } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import { forkJoin, lastValueFrom, map, Observable } from "rxjs";
import { PageInfoService } from "src/app/_metronic/layout";
import { CartService } from "src/app/services/api/carts.service";
import { CourseService } from "src/app/services/api/courses.service";
import { ToastService } from "src/app/services/toast-service/toast.service";
import { AuthService } from "../../auth";
import { AuthUtilitiesService } from "../../auth/services/auth-utilities.service";
import { CommonModule } from "@angular/common";
import { TranslateModule } from "@ngx-translate/core";
import { SharedModule } from "src/shared/shared.module";
import { environment } from "src/environments/environment";

@Component({
  selector: "app-course-categories",
  imports: [SharedModule, CommonModule, RouterModule, TranslateModule],
  templateUrl: "./course-categories.component.html",
  styleUrl: "./course-categories.component.scss",
})
export class CourseCategoriesComponent implements OnInit {
  #route = inject(ActivatedRoute);
  #fb = inject(FormBuilder);
  #courseService = inject(CourseService);
  #router = inject(Router);

  #toastService = inject(ToastService);

  #pageInfoService = inject(PageInfoService);
  #authService = inject(AuthService);

  form: FormGroup;
  dataSource: any;
  columns: any;
  isLoading = signal(true);
  tableLoading = signal(true);

  fileUrl = environment.apiFileUrl;

  category_id = signal<string | undefined>(undefined);

  constructor() {
    this.#route.paramMap
      .pipe(map((p) => p.get("category_id") ?? ""))
      .subscribe((id) => this.category_id.set(id));
  }

  ngOnInit(): void {
    this.initColumns();
    this.buildForm();
    this.getMasterData();
  }

  buildForm() {
    this.form = this.#fb.group({
      datatable: true,
      page: 1,
      order: "best_seller desc",
      limit: 4,
      keywords: null,
      education_id: null,
      semester_id: null,
      course_category_id: this.category_id(),
      course_category_ids: null,
    });
  }

  initColumns() {
    this.columns = [
      {
        prop: "name",
        sub_prop: "bill_date",
        headerClass: "w-350px min-w-150px",
        propClass: "text-hover-primary",
        sortable: false,
      },
    ];
  }

  _setLoading(value: boolean, time: number = 500) {
    setTimeout(() => {
      this.isLoading.update((_) => value);
    }, time);
  }

  getMasterData() {
    const params = {
      is_recommend: true,
      is_disabled: false,
    };

    this._setLoading(true, 0);
    forkJoin([
      this.#courseService.getCourseCategory(
        +this.category_id()!
      ) as Observable<any>,
      this.#courseService.getCourses(
        this.form.getRawValue()
      ) as Observable<any>,
    ]).subscribe({
      next: ([category, datatable]) => {
        this.dataSource = datatable;
        this.setBreadCrumb("คอร์สเรียน : " + category.name);
      },
      // An id that names no category errors, and RxJS never calls `complete`
      // after an error — so the page sat on its spinner forever and rendered
      // header, footer and nothing else. Fall through to the empty state.
      error: () => {
        this.dataSource = { data: [], total: 0, total_page: 1, page: 1, limit: 4, offset: 0 };
        this._setLoading(false, 1000);
        this.tableLoading.update((_) => false);
      },
      complete: () => {
        this._setLoading(false, 1000);
        this.tableLoading.update((_) => false);
      },
    });
  }

  async fetchData() {
    this.tableLoading.update((_) => true);
    try {
      const datatable = await lastValueFrom(this.#courseService.getCourses(this.form.getRawValue()))
      this.dataSource = datatable;
      this.tableLoading.update((_) => false);

    } catch (error) {
      this.tableLoading.update((_) => false);

    }

  }

  onRowClick(row: any) {
    // The course's own address, the same one the catalogue and the landing page
    // use. The old target put the id in a query param on a path that had no
    // course segment, which made the URL unshareable and untypeable.
    this.#router.navigate(["/courses", row.id]);
  }
  onTableChange(event: Event) {
    this.form.patchValue(event);
    this.fetchData()
  }

  setBreadCrumb(name: string) {
    this.#pageInfoService.updateTitle(name);
  }
}
